import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get } from '../cms-content-api-service';
export const selectCmsTjenesteTekster = (state, name) => state.cmsTjenesteTekster[getTjenesteTeksterKey(name, state.language.currentLanguage)];
export const selectCmsTjenesteTekstForHjelpetekstId = (state, name, hjelpetekstId) => {
    if (typeof hjelpetekstId === 'string') {
        return selectCmsTjenesteTekster(state, name)?.data?.hjelpetekster?.spesifikkeHjelpetekster?.find(t => t.id?.toLowerCase() === hjelpetekstId?.toLowerCase());
    }
    return selectCmsTjenesteTekster(state, name)?.data?.hjelpetekster?.spesifikkeHjelpetekster?.find(t => hjelpetekstId.map(h => h.toLowerCase()).includes(t.id?.toLowerCase()));
};
export const selectFeedbackResources = (state, name) => selectCmsTjenesteTekster(state, name)?.data?.feedback;
export const selectCmsTjenesteContent = (state, name) => selectCmsTjenesteTekster(state, name)?.data?.tjenesteContent;
export const selectWordExplanation = (state, name, word) => selectCmsTjenesteTekster(state, name)?.data?.oppslagsverk?.ordforklaringer?.find(o => {
    const wordTrimmed = word.trim().toLowerCase();
    const synonymList = o.synonyms?.split(';').map(s => s.trim().toLowerCase());
    return wordTrimmed === o.title?.trim().toLowerCase() || synonymList?.includes(wordTrimmed);
});
export const getCanShowHjelpetekstInPageHeader = (state, tjenesteNavn, hjelpetekstId) => {
    const spesifikkHjelpeTekst = hjelpetekstId ? selectCmsTjenesteTekstForHjelpetekstId(state, tjenesteNavn, hjelpetekstId) : undefined;
    const tjenesteTekster = selectCmsTjenesteTekster(state, tjenesteNavn);
    return !!spesifikkHjelpeTekst?.hjelpetekst || (tjenesteTekster?.data?.hjelpetekster?.hjelpetekst?.length ?? 0) > 0;
};
const shouldFetchTjenesteTekster = (state, name, language) => {
    const key = getTjenesteTeksterKey(name, language);
    const cmsTjenesteTekster = state.cmsTjenesteTekster[key];
    return cmsTjenesteTekster?.status !== 'loading' && cmsTjenesteTekster?.status !== 'succeeded';
};
/**
 * Hent nye tjenestetekster basert på tjenestenavn og language
 */
export const fetchTjenesteTekster = createAsyncThunk('cmsTjenesteTekster/fetchTjenesteTekster', async ({ name, language }, { rejectWithValue }) => {
    try {
        const tjenesteTekster = await get('tjeneste', {
            navn: name,
            languageCode: language,
        });
        if (tjenesteTekster) {
            return tjenesteTekster;
        }
        throw new Error(`Kunne ikke hente tjenestetekster for ${name} på ${language}`);
    }
    catch {
        return rejectWithValue(`Kunne ikke hente tjenestetekster for ${name} på ${language}`);
    }
}, {
    condition: ({ name, language }, { getState }) => {
        return shouldFetchTjenesteTekster(getState(), name, language);
    },
});
export const getTjenesteTeksterKey = (name, languageCode) => `${name}.${languageCode}`;
const initialState = {};
const cmsTjenesteTeksterSlice = createSlice({
    name: 'cmsTjenesteTekster',
    initialState,
    reducers: {
        addSpesifikkeHjelpetekster(state, action) {
            const key = getTjenesteTeksterKey(action.payload.name, action.payload.language);
            state[key] = {
                status: 'succeeded',
                data: {
                    ...state[key]?.data,
                    hjelpetekster: {
                        ...state[key]?.data?.hjelpetekster,
                        spesifikkeHjelpetekster: [
                            ...(state[key]?.data?.hjelpetekster?.spesifikkeHjelpetekster ?? []),
                            ...action.payload.spesifikkeHjelpetekster,
                        ],
                    },
                },
            };
        },
        setDefaultHjelpetekst(state, action) {
            const key = getTjenesteTeksterKey(action.payload.name, action.payload.language);
            state[key] = {
                status: 'succeeded',
                data: {
                    ...state[key]?.data,
                    hjelpetekster: {
                        ...state[key]?.data?.hjelpetekster,
                        overskift: action.payload.overskift,
                        hjelpetekst: action.payload.hjelpetekst,
                    },
                },
            };
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchTjenesteTekster.pending, (state, action) => {
            const key = getTjenesteTeksterKey(action.meta.arg.name, action.meta.arg.language);
            state[key] = {
                status: 'loading',
                data: null,
            };
        })
            .addCase(fetchTjenesteTekster.fulfilled, (state, action) => {
            const key = getTjenesteTeksterKey(action.meta.arg.name, action.meta.arg.language);
            state[key] = {
                status: 'succeeded',
                data: action.payload,
            };
        })
            .addCase(fetchTjenesteTekster.rejected, (state, action) => {
            const key = getTjenesteTeksterKey(action.meta.arg.name, action.meta.arg.language);
            state[key] = {
                status: 'failed',
                data: null,
            };
        });
    },
});
export const { addSpesifikkeHjelpetekster, setDefaultHjelpetekst } = cmsTjenesteTeksterSlice.actions;
export default cmsTjenesteTeksterSlice.reducer;
